import React, { Component, createRef, RefObject } from 'react';
import { RouteComponentProps } from 'react-router';
import { ApiUrls } from '@api/apiUrls';
import { IApiCopyType, IApiGroupMappingType } from '@api/interfaces/data-contracts';
import { apiUserCopyTrading } from '@api/services/user/copyTrading/userCopyTrading.apiService';
import { FIELDS_INVESTOR, Form } from '@components-member/TradingAccounts/CopyTradingAccounts/InvestorFormFields';
import { EnumUTACopyTAInvestorFormField } from '@components-member/TradingAccounts/CopyTradingAccounts/enums';
import {
  configureAccountSettings,
  updateInvestorMarginLevelFields,
} from '@components-member/socialTrading/shared/accountSettingsService';
import { getCopyTradingSettings } from '@components-member/socialTrading/shared/requests';
import {
  copyTypeOptionsHandler,
  setLotForEquityAndBalanceCopyTypeToZero,
  setMarginPercentageLevelToZero,
} from '@components-member/socialTrading/utils';
import ErrorView, { IErrorViewModel } from '@components-shared/errorView';
import { IFormField } from '@components-shared/forms/formField.interface';
import { FormUtils } from '@components-shared/forms/forms.utils';
import { NotificationDialog } from '@components-shared/modal';
import Preloader from '@components-shared/preloader';
import { IAccount } from '@components-shared/shared.interfaces';
import { EmptyObject } from '@components-shared/shared.interfaces';
import {
  isLotCopyTypeCheck,
  getInfoAboutOpenOrders,
  leverageFieldHandler,
} from '@components-shared/socialTrading/utils';
import { keyMap } from '@locales/lokalise/keyMap';
import { fetchApi } from 'api/fetchApi';
// @ts-ignore: remove after translating into typescript
import { getInitialState } from 'components/shared/forms/formGenerator';
import { handleError } from 'utils/errors';

type AccountNewSettings = {
  lot?: string;
  copyType?: IApiCopyType;
  accountType: string;
};

interface IInvestorSettingsFormProps extends RouteComponentProps {
  investorId: number | null;
}

export interface IInvestorAccountDetails {
  id: number;
  copyType: IApiCopyType;
  accountTypeDisplayName: string;
  lot?: number;
  changeAllocationMethodAllowed: boolean;
  marginStopCopyLevel: number;
  stopCopyViaMarginLevel: number | string;
  percentageLossStopCopyLevel: number;
  leverages: number[];
  leverage: number;
  groupMappingType: IApiGroupMappingType;
}

interface IForm {
  [key: string]: string | number | undefined | IApiCopyType | null;
  lot?: string;
  accountType: string;
  copyType: IApiCopyType;
  marginStopCopyLevel: string;
  stopCopyViaMarginLevel: number | null;
  percentageLossStopCopyLevel: string;
}

interface IInvestorSettingsFormState extends IErrorViewModel {
  account: IInvestorAccountDetails | EmptyObject;
  processing: boolean;
  form: IForm;
  isUpdateInvestorLeverageAllowed: boolean;
  availableAllocationMethods: IApiCopyType[];
  isMarginStopCopyLevelSupported: boolean;
  isStopCopyViaMarginLevelSupported: boolean;
}

export class InvestorSettingsForm extends Component<IInvestorSettingsFormProps, IInvestorSettingsFormState> {
  formRef: RefObject<{ update: (fields: IFormField[]) => void }>;

  fields: IFormField[];

  constructor(props: IInvestorSettingsFormProps) {
    super(props);

    this.state = {
      account: {},
      form: { ...getInitialState([...FIELDS_INVESTOR]) },
      processing: true,
      errMsg: '',
      isUpdateInvestorLeverageAllowed: false,
      availableAllocationMethods: [],
      isMarginStopCopyLevelSupported: false,
      isStopCopyViaMarginLevelSupported: false,
    };

    this.fields = [...FIELDS_INVESTOR];
    this.formRef = createRef();
  }

  async componentDidMount(): Promise<void> {
    await this.getDetails();
  }

  getDetails = async (): Promise<void> => {
    const { investorId } = this.props;

    try {
      const [investorResponse, socialTradingSettingsResponse] = await Promise.all([
        fetchApi.get<IInvestorAccountDetails>(ApiUrls.tradingAccountUser.getCopyTradingInvestorAccount, {
          params: { id: investorId },
        }),
        getCopyTradingSettings(),
      ]);

      const { availableAllocationMethods, isMarginStopCopyLevelSupported, isStopCopyViaMarginLevelSupported } =
        await configureAccountSettings({
          value: investorId,
          fetchSettings: () => apiUserCopyTrading.getInvestorSettings({ masterAccountId: null, investorId }),
          form: this.formRef,
          onChange: this.onChange,
          fields: this.fields,
        });
      const investorAccount = investorResponse.data;

      //for null value it needs to set it to empty to prevent entering invalid symbols
      if (!investorAccount.stopCopyViaMarginLevel) {
        investorAccount.stopCopyViaMarginLevel = '';
      }

      const { isUpdateInvestorLeverageAllowed } = socialTradingSettingsResponse.data;

      this.setState(
        {
          account: investorAccount,
          isUpdateInvestorLeverageAllowed,
          availableAllocationMethods,
          isMarginStopCopyLevelSupported,
          isStopCopyViaMarginLevelSupported,
        },
        this.formInitialization,
      );
    } catch (error) {
      handleError(keyMap.sharedErrorsFailedToGetRecord, error);
      this.setState({ errMsg: error.response.data });
    } finally {
      this.setState({ processing: false });
    }
  };

  formInitialization = () => {
    const {
      account,
      isUpdateInvestorLeverageAllowed,
      availableAllocationMethods,
      isMarginStopCopyLevelSupported,
      isStopCopyViaMarginLevelSupported,
    } = this.state;

    this.lotFieldHandler(account.copyType);

    this.copyTypeFieldHandler(account.changeAllocationMethodAllowed);

    const leveragesOption = account.leverages.map((leverage) => ({ value: leverage }));

    leverageFieldHandler(account.leverage, this.fields, isUpdateInvestorLeverageAllowed, leveragesOption);

    copyTypeOptionsHandler(availableAllocationMethods, this.fields, account.copyType);
    updateInvestorMarginLevelFields({
      account,
      isMarginStopCopyLevelSupported,
      isStopCopyViaMarginLevelSupported,
      fields: this.fields,
    });
    this.setInvestorFormValues(account);
  };

  setInvestorFormValues = (account: IInvestorAccountDetails | EmptyObject): void => {
    const {
      leverage,
      accountTypeDisplayName,
      copyType,
      lot,
      marginStopCopyLevel,
      stopCopyViaMarginLevel,
      percentageLossStopCopyLevel,
    } = account;
    const isLotCopyType = isLotCopyTypeCheck(copyType);

    const defaultFormValues = {
      accountType: accountTypeDisplayName,
      copyType,
      marginStopCopyLevel,
      stopCopyViaMarginLevel,
      percentageLossStopCopyLevel,
      leverage,
    };

    if (!isLotCopyType) {
      this.updateFormState({ ...defaultFormValues });

      return;
    }

    this.updateFormState({ ...defaultFormValues, lot: String(lot) });
  };

  updateFormState = (data: AccountNewSettings): void => {
    const { form } = this.state;

    this.setState({ form: { ...form, ...data } });
  };

  lotFieldHandler = (copyType: IApiCopyType): void => {
    const lotFieldIdx = FormUtils.getFieldIndex(this.fields, EnumUTACopyTAInvestorFormField.lot);

    FormUtils.updateFormField(lotFieldIdx, { isHidden: !isLotCopyTypeCheck(copyType) }, this.fields);

    this.formRef.current?.update(this.fields);
  };

  copyTypeFieldHandler = (isEnabledField: boolean) => {
    const copyTypeIdx = FormUtils.getFieldIndex(this.fields, EnumUTACopyTAInvestorFormField.copyType);

    FormUtils.updateFormField(copyTypeIdx, { attributes: { disabled: !isEnabledField } }, this.fields);
  };

  updateInvestorAccountHandler = async (): Promise<void> => {
    const { form, account } = this.state;

    setLotForEquityAndBalanceCopyTypeToZero(form);
    setMarginPercentageLevelToZero(form);

    const updateData = { ...form };

    return await this.updateInvestorAccountRequest(
      ApiUrls.tradingAccountUser.updateCopyTradingInvestorAccount,
      ApiUrls.tradingAccountUser.getCopyTradingInvestorOpenOrders,
      account.id,
      updateData,
    );
  };

  updateInvestorAccountRequest = async (
    updatePath: string,
    getOrdersPath: string,
    id: number,
    data: AccountNewSettings,
  ): Promise<void> => {
    try {
      const response = await getInfoAboutOpenOrders(getOrdersPath, id);
      const openOrders = response.data;

      if (openOrders) {
        handleError(keyMap.sharedErrorsFailedToSaveSettings, null, keyMap.tradingAccountsChangeSettingsErrorMessage);
        return;
      }

      await fetchApi.post<IAccount>(updatePath, data, { params: { id } });
      NotificationDialog.show(keyMap.tradingAccountsChangeSettingsSuccessMessage);
    } catch (error) {
      handleError(keyMap.sharedErrorsFailedToSaveSettings, error);
    }
  };

  onChange = (name: string, value?: string | number): void => {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        [name]: value,
      },
    }));
  };

  render() {
    const { form, processing, errMsg } = this.state;

    if (processing) {
      return <Preloader />;
    }

    if (errMsg) {
      return <ErrorView error={errMsg} />;
    }

    const actions = [
      {
        title: keyMap.tradingAccountsChangeSettings,
        onClick: this.updateInvestorAccountHandler,
        type: 'submit',
        disabled: false,
      },
    ];

    return (
      <Form
        {...this.props}
        className="form-box form-box--columns form--social"
        form={form}
        actions={actions}
        ref={this.formRef}
        onChange={this.onChange}
        lotFieldHandler={this.lotFieldHandler}
      />
    );
  }
}
